import { useMemo, useState, type MouseEvent } from 'react';
import type { Page } from './utils';
import { calcSeries, fmtCompact, fmtInt, fmtTime, cityOf } from './utils';
import s from './BroadcastDetailsModal.module.css';

interface Props { pages: Page[]; d0: number; d1: number; showDelivered?: boolean }
type Mode = 'perMin' | 'cum';

const toPoints = (arr: number[], max: number) =>
  arr.map((v, i) => `${((i / Math.max(arr.length - 1, 1)) * 1000).toFixed(1)},${(100 - (v / max) * 98).toFixed(2)}`).join(' ');

export function ProgressChart({ pages, d0, d1, showDelivered = true }: Props) {
  const [mode, setMode] = useState<Mode>('perMin');
  const [hx, setHx] = useState<number | null>(null);

  const series = useMemo(() => calcSeries(pages, d0, d1), [pages, d0, d1]);
  const v = series[mode];
  const max = Math.max(...v.sent, 1) * 1.08;
  const fmax = Math.max(...v.fail, 1) * 1.1;
  const pct = (sec: number) => `${((sec - d0) / (d1 - d0)) * 100}%`;

  // Passos "redondos" de tempo. O passo era fixo em 30min para qualquer
  // janela acima de 4h, então um grupo de 26h desenhava 52 rótulos no mesmo
  // espaço e viravam "01:302:003:0...". Aqui o passo sobe até caber.
  const PASSOS = [300, 600, 900, 1800, 3600, 7200, 10800, 21600, 43200, 86400];
  const MAX_ROTULOS = 12;

  const xTicks = useMemo(() => {
    const span = Math.max(d1 - d0, 1);
    const passo = PASSOS.find((p) => span / p <= MAX_ROTULOS) ?? PASSOS[PASSOS.length - 1];
    const out: number[] = [];
    for (let t = Math.ceil(d0 / passo) * passo; t <= d1; t += passo) out.push(t);
    return out;
  }, [d0, d1]);

  const schedMarks = useMemo(() => {
    const m = new Map<number, Set<string>>();
    pages.forEach((p) => m.set(p.sch, (m.get(p.sch) ?? new Set()).add(cityOf(p.tz))));
    const todas = [...m.entries()].sort((a, b) => a[0] - b[0]);

    // Um grupo agenda por fuso, então são dezenas de marcações e os rótulos
    // se sobrepunham num borrão. Marcações a menos de 6% da largura viram uma
    // só, e o rótulo conta quantas foram agrupadas.
    const span = Math.max(d1 - d0, 1);
    const minimo = span * 0.06;
    const juntas: { t: number; cities: Set<string>; n: number }[] = [];
    for (const [t, cities] of todas) {
      const ultima = juntas[juntas.length - 1];
      if (ultima && t - ultima.t < minimo) {
        cities.forEach((c) => ultima.cities.add(c));
        ultima.n += 1;
      } else {
        juntas.push({ t, cities: new Set(cities), n: 1 });
      }
    }

    return juntas.map(({ t, cities, n }) => {
      const nomes = [...cities];
      const texto = nomes.length > 2 ? `${nomes[0]} +${nomes.length - 1}` : nomes.join(' · ');
      return {
        t,
        label: `${texto} ${fmtTime(t)}`,
        title: n > 1 ? `${n} agendamentos · ${nomes.join(', ')}` : nomes.join(', '),
      };
    });
  }, [pages, d0, d1]);

  const sentPts = toPoints(v.sent, max);
  const failPts = toPoints(v.fail, fmax);
  const hi = hx == null ? 0 : Math.round(hx * (series.n - 1));
  const tSec = d0 + hi * 60;
  const active = hx == null ? 0 : pages.filter((p) => p.s <= tSec + 60 && p.e >= tSec).length;

  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHx(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)));
  };
  const hxPct = `${(hx ?? 0) * 100}%`;

  return (
    <div className={`${s.card} ${s.grow}`}>
      <div className={s.cardHead}>
        <span className={s.cardTitle}>Progresso do envio</span>
        <span className={s.cardSub}>BRT · {mode === 'perMin' ? 'por minuto' : 'acumulado'}</span>
        <div className={s.legend}>
          <span><i className={s.lnSolid} style={{ background: '#2563eb' }} />Enviados</span>
          {showDelivered && <span><i className={s.lnDash} />Entregues</span>}
          <span><i className={s.lnSolid} style={{ background: '#ef4444' }} />Falhas</span>
        </div>
        <div className={s.seg}>
          {([['perMin', 'Por minuto'], ['cum', 'Acumulado']] as const).map(([id, label]) => (
            <button key={id} type="button" className={mode === id ? s.segOn : undefined} onClick={() => setMode(id)}>{label}</button>
          ))}
        </div>
      </div>

      <div className={s.chartGrid}>
        <div className={s.yAxis}>
          {[max, (max * 2) / 3, max / 3, 0].map((t, i) => <span key={i}>{fmtCompact(t)}</span>)}
        </div>
        <div className={s.plot} onMouseMove={onMove} onMouseLeave={() => setHx(null)}>
          {schedMarks.map((m, i) => (
            <div key={m.t} className={s.schedMark} style={{ left: pct(m.t) }} title={m.title}>
              {/* Alterna a altura: mesmo agrupadas, duas marcações vizinhas
                  têm rótulo mais largo que o espaço entre elas. */}
              <span style={{ top: i % 2 ? 16 : 2 }}>{m.label}</span>
            </div>
          ))}
          <svg viewBox="0 0 1000 100" preserveAspectRatio="none" className={s.svg}>
            <polygon points={`0,100 ${sentPts} 1000,100`} fill="#2563eb" fillOpacity={0.07} />
            <polyline points={sentPts} fill="none" stroke="#2563eb" strokeWidth={2} vectorEffect="non-scaling-stroke" />
            {showDelivered && (
              <polyline points={toPoints(v.deliv, max)} fill="none" stroke="#6366f1" strokeWidth={2} strokeDasharray="5 4" vectorEffect="non-scaling-stroke" />
            )}
          </svg>
          {hx != null && (
            <>
              <div className={s.crosshair} style={{ left: hxPct }} />
              <div className={s.dot} style={{ left: hxPct, top: `${100 - (v.sent[hi] / max) * 98}%`, background: '#2563eb' }} />
              {showDelivered && <div className={s.dot} style={{ left: hxPct, top: `${100 - (v.deliv[hi] / max) * 98}%`, background: '#6366f1' }} />}
              <div className={s.tooltip} style={{ left: hxPct, transform: hx > 0.7 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)' }}>
                <b>{fmtTime(tSec)} BRT</b>
                <span><em style={{ color: '#93c5fd' }}>Enviados</em><b>{fmtInt(v.sent[hi])}</b></span>
                {showDelivered && <span><em style={{ color: '#c7d2fe' }}>Entregues</em><b>{fmtInt(v.deliv[hi])}</b></span>}
                <span><em style={{ color: '#fca5a5' }}>Falhas</em><b>{fmtInt(v.fail[hi])}</b></span>
                <span className={s.tipSep}><em>Páginas rodando</em><b>{active}</b></span>
              </div>
            </>
          )}
        </div>

        <div className={s.failLabel}><b>Falhas</b><span>máx {fmtCompact(Math.max(...v.fail))}</span></div>
        <div className={s.failPlot} onMouseMove={onMove} onMouseLeave={() => setHx(null)}>
          <svg viewBox="0 0 1000 100" preserveAspectRatio="none" className={s.svg}>
            <polygon points={`0,100 ${failPts} 1000,100`} fill="#ef4444" fillOpacity={0.15} />
            <polyline points={failPts} fill="none" stroke="#ef4444" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
          </svg>
          {hx != null && <div className={s.crosshair} style={{ left: hxPct }} />}
        </div>

        <div />
        <div className={s.xAxis}>
          {xTicks.map((t) => <span key={t} style={{ left: pct(t) }}>{fmtTime(t)}</span>)}
        </div>
      </div>
    </div>
  );
}
